import { useState, useMemo } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import * as Yup from "yup";
import { useForm } from "react-yup";

import { yupLocale, FormErrors, PageEnvelope, CustomError } from "@skv/core";
import { useRootViewDispatch, useViewModel } from "@skv/state";
import { useUserDispatch } from "~/state";
import { Field, TextArea } from "~/ui";
import InPageAlert, {
  InPageAlertVariant,
  InPageAlertProps,
} from "~/ui/Alerts/InPageAlert";

Yup.setLocale(yupLocale);

const SCHEMA = Yup.object({
  form: Yup.object({
    title: Yup.string().required().label("Role title").max(100),
    organisation: Yup.string().required().label("Organisation name").max(100),
    when: Yup.string().required().label("When").max(100),
    description: Yup.string().required().label("Role description").max(500),
  }),
}).defined();

interface ExperienceFormModel {
  id: number;
  title: string;
  organisation: string;
  when: string;
  description: string;
  dateCreated: string;
}
interface ExperienceViewModel {
  didSave: boolean;
  form: ExperienceFormModel;
  formErrors: FormErrors<ExperienceFormModel>;
}

export const AddExperience = () => {
  const { updateUser } = useUserDispatch();
  const dispatch = useRootViewDispatch();
  const history = useHistory();
  const { id } = useParams<{
    id?: string;
  }>();

  const [alert, setAlert] = useState<InPageAlertProps>({
    variant: InPageAlertVariant.Error,
    message: "",
    show: false,
  });

  const isAdd = id === undefined;

  const [{ data, error, status }] = useViewModel<ExperienceViewModel>({
    noFetch: isAdd,
    onClientLoad: (data) => {
      setValues((v) => ({
        ...v,
        form: {
          ...v.form,
          ...data.form,
        },
      }));
    },
  });

  const {
    field,
    setValues,
    FormProvider,
    createSubmitHandler,
    isSubmitting,
    setSubmitting,
  } = useForm({
    defaultValues: () => {
      console.log(data);

      if (data?.form) {
        return {
          form: {
            ...data.form,
          },
        };
      }
    },
    validationSchema: SCHEMA,
  });

  const handleDelete = (e) => {
    e.preventDefault();

    fetch(`/my-activity/experience/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then<PageEnvelope<ExperienceViewModel>>((res) => {
        if (res.ok) {
          return res.json();
        }

        throw Error("Unhandled network error");
      })
      .then(({ success, errorCode, errorMessage }) => {
        if (success) {
          updateUser((prev) => ({
            ...prev,
            experienceCount: prev.experienceCount - 1,
          }));
          dispatch({
            type: "REMOVE_CACHED_VIEWS",
            payload: [
              "/my-activity/experiences",
              `/my-activity/experience/${id}`,
            ],
          });
          history.replace("/my-activity/experiences");
          return;
        }

        throw new CustomError(errorMessage, errorCode);
      })
      .catch((err: CustomError | Error) => {
        /* TODO : Probs better error messages */
        if (err instanceof CustomError) {
          switch (err.errorCode) {
            case "error:experience:delete":
              setAlert({
                variant: InPageAlertVariant.Error,
                message:
                  "We were not able to delete this experience. Please try again.",
                show: true,
              });
              break;
            default:
              break;
          }
        }
        setAlert({
          variant: InPageAlertVariant.Error,
          message:
            "We were not able to delete this experience. Please try again.",
          show: true,
        });
      });
  };

  const handleSubmit = useMemo(() => {
    return createSubmitHandler((v) => {
      fetch("/my-activity/experience", {
        method: isAdd ? "POST" : "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(v),
      })
        .then<PageEnvelope<ExperienceViewModel>>((res) => {
          if (res.ok) {
            return res.json();
          }

          throw Error("Unhandled network error");
        })
        .then(({ data, success, errorCode, errorMessage }) => {
          if (success) {
            updateUser((prev) => ({
              ...prev,
              experienceCount: prev.experienceCount + 1,
            }));

            dispatch({
              type: "REMOVE_CACHED_VIEWS",
              payload: [
                "/my-activity/experiences",
                `/my-activity/experience/${data.form.id}`,
              ],
            });
            if (isAdd) {
              history.replace("/my-activity/experiences");
            } else {
              history.push("/my-activity/experiences");
            }
            return;
          }

          throw new CustomError(errorMessage, errorCode, data);
        })
        .catch((err: Error | CustomError<ExperienceViewModel>) => {
          function isCustomErrorOfExperienceViewModel(
            err: Error | CustomError<ExperienceViewModel>
          ): err is CustomError<ExperienceViewModel> {
            if (err instanceof CustomError && "formErrors" in err.meta) {
              return true;
            }

            return false;
          }

          if (isCustomErrorOfExperienceViewModel(err)) {
            switch (err.errorCode) {
              case "error:formvalidation": {
                return setAlert({
                  variant: InPageAlertVariant.Error,
                  message: (
                    <>
                      Please fix the errors listed below.
                      <ul className="mb-0">
                        {Object.entries(err.meta.formErrors).map((n, idx) =>
                          n[1].map((x, idx2) => (
                            <li key={`${idx}__${idx2}`}>{x}</li>
                          ))
                        )}
                      </ul>
                    </>
                  ),
                  show: true,
                });
              }
            }
          }

          setAlert({
            variant: InPageAlertVariant.Error,
            message:
              "We were not able to update your experience. Please try again.",
            show: true,
          });

          setSubmitting(false);
        });
    });
  }, [createSubmitHandler, isAdd, setSubmitting, history, dispatch]);

  if (status === "loading") {
    return <p className="mt-3">Loading, please wait...</p>;
  }

  return (
    <>
      <div className="bg-light px-3 py-4">
        <h1>{isAdd ? "Add" : "Edit"} volunteer experience</h1>
        <p>Enter your details of your experience below.</p>
        <InPageAlert {...alert} />
        <FormProvider>
          <form method="POST" onSubmit={handleSubmit}>
            <Field
              label="Role title"
              id="form.title"
              name="form.title"
              {...field}
            />
            <Field
              label="Organisation name"
              id="form.organisation"
              name="form.organisation"
              placeholder="e.g. the name of the organisation you volunteered for"
              {...field}
            />
            <Field
              label="When"
              id="form.when"
              name="form.when"
              placeholder="e.g. 20 November 2017; or Jan 2016 to July 2018"
              {...field}
            />
            <TextArea
              label="Role description"
              id="form.description"
              name="form.description"
              rows={5}
              {...field}
            />

            <div className="d-flex flex-wrap align-items-start mt-4 gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                style={{
                  width: 120,
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : isAdd ? "Add" : "Save"}
              </button>
              <Link
                to={(loc) => ({
                  pathname: "/my-activity/experiences",
                  state: loc.state,
                })}
                className="btn btn-secondary px-4"
              >
                Cancel
              </Link>
              {!isAdd && (
                <button
                  type="button"
                  className="ml-auto btn btn-link"
                  onClick={handleDelete}
                >
                  Delete Experience
                </button>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </>
  );
};
